import React, { useEffect, useState } from "react";
import Header from "../../Components/Header/Header";
import PageHeadings from "../../Components/PageHeadings/PageHeadings";
import FiltersSidebar from "../../Components/FiltersSidebar/FiltersSidebar";
import DashboardsCards from "../../Components/DashboardCards/DashboardsCards";
import Footer from "../../Components/Footer/Footer";
import { useLocation, useParams } from "react-router-dom";
import { APIUrl, APIUrlToken } from "../../Utils/Utils";
import axios from "axios";
import { toast } from "react-toastify";

const Dashboard = ({ mode = "both" }) => {
  // NEW: read IDs from the URL (/:teamId and /:teamId/:playerId)
  const { teamId, playerId } = useParams();

  const [skip, setSkip] = useState(0); // pagination offset (server: skip)
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playersCount, setPlayersCount] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [open, setOpen] = useState(false);
  const [maxWeight, setMaxWeight] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [apiCalled, setApiCalled] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loadedPlayer, setLoadedPlayer] = useState(null);
  const showContribute = mode !== "collab"; // true unless collab-only
  const showCollab = mode !== "contribute"; // true unless contribute-only
  const location = useLocation();
  const { sportIdHome, sportLabel } = location.state || {};

  // Hard-coded sport labels (since /get_player doesn't return sport_name)
  const sportMap = {
    1: "Men's Basketball",
    2: "Men's Football",
    3: "Women's Basketball",
  };

  const [filterState, setFilterState] = useState({
    // sport: { value: 2, label: sportMap[2] },
    sport: sportIdHome
      ? { value: sportIdHome, label: sportLabel }
      : { value: 2, label: sportMap[2] },
    conference: "",
    team: teamId ? { value: Number(teamId), label: "" } : "",
    class: "",
    position: "",
    minAmount: 0,
    maxAmount: Number(maxWeight) || 100,
  });

  const [playerFilter, setPlayerFilter] = useState("");

  // Open details (by IDs)
  const handleViewDetail = (player) => {
    const url = `/DetailsView/${player.player_id}/${player.team_id}?mode=${mode}`;
    window.open(url, "_blank");
  };

  const handleViewFanEngagement = (player) => {
    const url = `/FanExperience/${player.player_id}/${player.team_id}?mode=${mode}`;
    window.open(url, "_blank");
  };
  const handleViewBrandEngagement = (player) => {
    const url = `/BrandPartnership/${player.player_id}/${player.team_id}?mode=${mode}`;
    window.open(url, "_blank");
  };
  // ─────────────────────────────────────────────────────────
  // A) Single player by IDs → /v1/get_player/{player_id}/{team_id}
  // ─────────────────────────────────────────────────────────
  const fetchSingleByIds = async () => {
    if (!teamId || !playerId) return;
    setLoading(true);
    try {
      const url = `${APIUrl()}/v1/get_player/${playerId}/${teamId}`;
      const { data } = await axios.get(url, {
        headers: { "X-API-Key": APIUrlToken() },
      });

      setPlayers([data]);
      setPlayersCount(1);
      setLoadedPlayer(data);
      setIsFiltered(true);

      // Fill filters from the player response
      setFilterState((prev) => ({
        ...prev,
        sport: data?.sport_id
          ? {
              value: Number(data.sport_id),
              label: sportMap[data.sport_id] || prev?.sport?.label || "",
            }
          : prev.sport,
        team: {
          value: Number(data.team_id),
          label: data.team_name || prev.team?.label || "",
        },
        conference: data?.conference
          ? { value: data.conference, label: data.conference }
          : prev.conference,
        position: data?.position
          ? { value: data.position, label: data.position }
          : prev.position,
        class: data?.player_class
          ? { value: data.player_class, label: data.player_class }
          : prev.class,
      }));

      setPlayerFilter(data.player_name || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Player not found");
      setPlayers([]);
      setPlayersCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // B) Team roster by ID → /v1/get_players_by_team/{team_id}
  //    (uses limit=21 & skip pagination)
  // ─────────────────────────────────────────────────────────
  const PAGE_SIZE = 21;

  const fetchTeamPlayersById = async (isFirst) => {
    if (!teamId) return;
    setLoading(true);
    try {
      if (isFirst) {
        setSkip(0);
        setPlayers([]);
        setPlayersCount(0);
        setApiCalled(false);
      }

      const url = `${APIUrl()}/v1/get_players_by_team/${teamId}?limit=${PAGE_SIZE}&skip=${
        isFirst ? 0 : skip
      }`;

      const { data: resp } = await axios.get(url, {
        headers: { "X-API-Key": APIUrlToken() },
      });

      const count = resp?.total_count || 0;
      const list = resp?.players || [];

      setPlayersCount(count);

      // Auto-fill filter labels from the first player (if available)
      const first = list?.[0];
      if (first) {
        setFilterState((prev) => ({
          ...prev,
          sport: first?.sport_id
            ? {
                value: Number(first.sport_id),
                label: sportMap[first.sport_id] || prev?.sport?.label || "",
              }
            : prev.sport,
          team: {
            value: Number(teamId),
            label: first?.team_name || prev.team?.label || "",
          },
          conference: first?.conference
            ? { value: first.conference, label: first.conference }
            : prev.conference,
        }));
      }

      // (Optional) sort for stable display
      const sorted = [...list]
        ?.sort((a, b) => a.team_name.localeCompare(b.team_name))
        ?.sort((a, b) =>
          a.team_name === b.team_name
            ? a.player_name.localeCompare(b.player_name)
            : 0
        );

      if (isFirst) {
        setPlayers(sorted);
      } else {
        const merged = [...players, ...sorted]
          ?.sort((a, b) => a.team_name.localeCompare(b.team_name))
          ?.sort((a, b) =>
            a.team_name === b.team_name
              ? a.player_name.localeCompare(b.player_name)
              : 0
          );
        setPlayers(merged);
      }

      setIsFiltered(true);
      setApiCalled(true);
    } catch (err) {
      // NOTE: your API raises 404 if no players found — surface the message
      toast.error(err?.response?.data?.detail || "Failed to load team players");
      setApiCalled(true);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // C) Generic filter-based fetch (fallback / landing)
  //     (kept as-is for non-ID pages; posts to common_filter_players)
  // ─────────────────────────────────────────────────────────
  const handleFilterData = (isFirst) => {
    setLoading(true);
    if (isFirst) {
      setSkip(0);
      setPlayers([]);
      setPlayersCount(0);
      setApiCalled(false);
    }

    const url = `${APIUrl()}/v1/v1/common_filter_players?limit=21&skip=${
      isFirst ? 0 : skip
    }`;

    // 🔑 Always include selected scope (team, conference) + name, etc.
    // const data = {};
    // if (filterState?.sport?.value) data.sport_id = filterState.sport.value;
    // if (filterState?.team?.value) data.team_id = Number(filterState.team.value);         // <- no !isSearch check
    // if (filterState?.conference?.value) data.conference = filterState.conference.value;  // <- no !isSearch check
    // if (filterState?.class?.value) data.player_class = filterState.class.value;
    // if (filterState?.position?.value) data.position = filterState.position.value;
    // if (playerFilter && playerFilter.trim()) data.player_name = playerFilter.trim();

    // // donation range
    // const min = Number.isFinite(filterState?.minAmount) ? filterState.minAmount : 0;
    // const max =
    //   Number.isFinite(filterState?.maxAmount)
    //     ? filterState.maxAmount
    //     : (Number(maxWeight) || 100);
    // data.min_donation = min;
    // data.max_donation = max;
    // 🔑 Always include current scope if selected
    const data = {};
    if (filterState?.sport?.value) data.sport_id = filterState.sport.value;
    if (filterState?.team?.value) data.team_id = Number(filterState.team.value); // <- no !isSearch
    if (filterState?.conference?.value)
      data.conference = filterState.conference.value; // <- no !isSearch
    if (filterState?.class?.value) data.player_class = filterState.class.value;
    if (filterState?.position?.value)
      data.position = filterState.position.value;
    if (playerFilter && playerFilter.trim())
      data.player_name = playerFilter.trim();

    // donation range (always send)
    // data.min_donation = Number.isFinite(filterState?.minAmount)
    //   ? filterState.minAmount
    //   : 0;
    // data.max_donation = Number.isFinite(filterState?.maxAmount)
    //   ? filterState.maxAmount
    //   : Number(maxWeight) || 100;

    axios
      .post(url, data, { headers: { "X-API-Key": APIUrlToken() } })
      .then((response) => {
        setIsFiltered(true);
        const resp = response?.data || {};
        const list = resp?.filtered_players || [];
        setPlayersCount(resp?.count || 0);

        const sorted = [...list]
          ?.sort((a, b) => a.team_name?.localeCompare(b.team_name))
          ?.sort((a, b) =>
            a.team_name === b.team_name
              ? a.player_name?.localeCompare(b.player_name)
              : 0
          );

        if (isFirst) {
          setPlayers(sorted);
        } else {
          setPlayers((prev) =>
            [...prev, ...sorted]
              ?.sort((a, b) => a.team_name?.localeCompare(b.team_name))
              ?.sort((a, b) =>
                a.team_name === b.team_name
                  ? a.player_name?.localeCompare(b.player_name)
                  : 0
              )
          );
        }

        setApiCalled(true);
        setTimeout(() => setLoading(false), 300);
      })
      .catch((error) => {
        setLoading(false);
        setApiCalled(true);
        toast.error(
          error?.response?.data?.error?.split(":")?.[1] ||
            error?.response?.data?.error ||
            "Failed to load players1"
        );
      });
  };

  // ─────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────

  // Initial load preference: IDs first → then generic filters
  useEffect(() => {
    if (teamId && playerId) {
      fetchSingleByIds();
    } else if (teamId) {
      fetchTeamPlayersById(true);
    } else {
      // no IDs: load a default list using filter flow
      if (isFiltered && skip > 0) {
        handleFilterData();
      } else {
        handleFilterData(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, playerId]);

  // Pagination for roster & generic list (not for single player)
  useEffect(() => {
    if (playerId) return; // no paging on single
    if (isFiltered && skip > 0) {
      if (teamId) fetchTeamPlayersById(false);
      else handleFilterData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  // Re-run when filters change (but not for single-player)
  // Re-run when filters change ONLY for the generic (no-team) view.
  useEffect(() => {
    if (!playerId && !teamId) {
      const t = setTimeout(() => {
        handleFilterData(true);
      }, 600);
      return () => clearTimeout(t);
    }
  }, [filterState]); // ✅ no re-fetch loop when teamId is present

  const handleNextClick = () => setSkip((prev) => prev + PAGE_SIZE);

  const handleSearchPlayers = () => {
    setSkip(0); // reset pagination

    if (teamId) {
      // you are on /:teamId — search within this team
      fetchTeamPlayersById(true);
    } else {
      // generic dashboard — use common_filter_players with current filters
      handleFilterData(true);
    }
  };

  const handleShowFilters = () => setShowFilters((prev) => !prev);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div>
      <Header />
      <div className="page-main-container">
        <PageHeadings showContribute={showContribute}/>
        <div className="filter-sidebar-plus-dashcards">
          {isMobile && (
            <button
              onClick={handleShowFilters}
              className="show-filter-btn-dash"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          )}
          {isMobile ? (
            showFilters ? (
              <FiltersSidebar
                setPlayers={setPlayers}
                loading={loading}
                setLoading={setLoading}
                setIsFiltered={setIsFiltered}
                skip={skip}
                filterState={filterState}
                setFilterState={setFilterState}
                handleFilterData={handleFilterData}
                setSkip={setSkip}
                maxWeight={maxWeight}
                setMaxWeight={setMaxWeight}
                handleSearchPlayers={handleSearchPlayers}
                playerFilter={playerFilter}
                setPlayerFilter={setPlayerFilter}
                loadedPlayer={loadedPlayer}
              />
            ) : null
          ) : (
            <FiltersSidebar
              setPlayers={setPlayers}
              loading={loading}
              setLoading={setLoading}
              setIsFiltered={setIsFiltered}
              skip={skip}
              filterState={filterState}
              setFilterState={setFilterState}
              handleFilterData={handleFilterData}
              setSkip={setSkip}
              maxWeight={maxWeight}
              setMaxWeight={setMaxWeight}
              handleSearchPlayers={handleSearchPlayers}
              playerFilter={playerFilter}
              setPlayerFilter={setPlayerFilter}
              loadedPlayer={loadedPlayer}
            />
          )}
          <DashboardsCards
            handleViewDetail={handleViewDetail}
            handleViewFanEngagement={handleViewFanEngagement}
            handleViewBrandEngagement={handleViewBrandEngagement}
            playersCount={playersCount}
            players={players}
            loading={loading}
            setLoading={setLoading}
            handleNextClick={handleNextClick}
            isFiltered={isFiltered}
            open={open}
            setOpen={setOpen}
            apiCalled={apiCalled}
            showContribute={showContribute}
            showCollab={showCollab}
            mode={mode}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
